import React, { useEffect, useMemo, useState } from 'react'

import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Stack from 'react-bootstrap/Stack'

import { formatCurrency } from '../../../../utilities/helpers'

import './donationCard.scss'

function TargetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M5 12l4 4L19 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function formatGivingLevelAmount(amount) {
  return `$${Number(amount).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })}`
}

function formatSectionList(sections) {
  if (!sections?.length) return ''
  if (sections.length === 1) return `Sec ${sections[0]}`
  if (sections.length === 2) {
    return `Sec ${sections[0]} or Sec ${sections[1]}`
  }
  return `${sections
    .slice(0, -1)
    .map((section) => `Sec ${section}`)
    .join(', ')}, or Sec ${sections[sections.length - 1]}`
}

function formatSatisfiedDate(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function buildSatisfiedSubtitle(satisfaction) {
  if (!satisfaction) {
    return 'Donation already satisfied for this account.'
  }

  const parts = []
  const amount = Number(satisfaction.recordedAmount)
  const recordedDate = formatSatisfiedDate(satisfaction.satisfiedAt)

  if (Number.isFinite(amount) && amount > 0) {
    parts.push(
      recordedDate
        ? `${formatCurrency(amount)} recorded ${recordedDate}`
        : formatCurrency(amount)
    )
  } else if (recordedDate) {
    parts.push(`Recorded ${recordedDate}`)
  }

  if (satisfaction.carriedOverFrom) {
    parts.push(`carried over from ${satisfaction.carriedOverFrom}`)
  }

  return parts.length > 0
    ? parts.join(' — ')
    : 'Donation already satisfied for this account.'
}

function ChevronRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function formatFundPickerSubtitle(rule) {
  const sections = (rule.triggerSections || []).join(', ')
  const coverage =
    rule.carryForward !== false
      ? 'covers all linked packages'
      : 'this package only'

  return sections ? `Sections ${sections} · ${coverage}` : coverage
}

function DonationFundPicker({ rules, onSelectRule }) {
  return (
    <Card className="card card--sm card-with-border donation-card donation-card--choose-fund background-tertiary-light">
      <Card.Body>
        <Stack direction="horizontal" gap={3} className="align-items-start">
          <div className="donation-card__icon donation-card__icon--target">
            <TargetIcon />
          </div>
          <div className="flex-grow-1 min-w-0">
            <Stack
              direction="horizontal"
              gap={2}
              className="heading--flex flex-sm flex-wrap align-items-center mb-1"
            >
              <Card.Title as="h2" className="card-title m-0">
                Donation required
              </Card.Title>
              <span className="donation-card__choose-badge text-upper text-upper-caption">
                Choose fund
              </span>
            </Stack>
            <p className="subtitle m-0">
              The selected seats trigger more than one donation rule. Pick a
              fund and choose an amount for each.
            </p>
          </div>
        </Stack>

        <Stack gap={2} className="donation-card__fund-options mt-3">
          {rules.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="donation-card__fund-option"
              onClick={() => onSelectRule(String(candidate.id))}
            >
              <div className="donation-card__fund-option-main">
                <span className="donation-card__fund-option-name">
                  {candidate.name}
                </span>
                <span className="donation-card__fund-option-detail">
                  {formatFundPickerSubtitle(candidate)}
                </span>
              </div>
              <div className="donation-card__fund-option-amount">
                <span>{formatGivingLevelAmount(candidate.minimum)}</span>
                <span className="text-upper text-upper-caption">Minimum</span>
              </div>
              <span className="donation-card__fund-option-chevron" aria-hidden>
                <ChevronRightIcon />
              </span>
            </button>
          ))}
        </Stack>
      </Card.Body>
    </Card>
  )
}

function ConfiguredDonationRuleCard({ configuredDonation }) {
  const amount = Number(configuredDonation.amount) || 0
  const subtitle = configuredDonation.externalPending
    ? amount > 0
      ? `${formatCurrency(amount)} — payment expected externally`
      : 'Marked pending — payment expected externally'
    : formatCurrency(amount)

  return (
    <Card className="card card--sm donation-card donation-card--satisfied">
      <Card.Body>
        <Stack direction="horizontal" gap={2} className="align-items-start">
          <div className="donation-card__icon donation-card__icon--success">
            <CheckIcon />
          </div>
          <div>
            <Stack
              direction="horizontal"
              gap={2}
              className="heading--flex flex-sm flex-wrap align-items-center"
            >
              <Card.Title as="h2" className="card-title m-0">
                {configuredDonation.ruleName}
              </Card.Title>
              <span className="donation-card__satisfied-badge text-upper text-upper-caption">
                {configuredDonation.externalPending ? 'Pending' : 'Added'}
              </span>
            </Stack>
            <p className="donation-card__satisfied-subtitle m-0 mt-1">
              {subtitle}
            </p>
          </div>
        </Stack>
      </Card.Body>
    </Card>
  )
}

function SatisfiedDonationRuleCard({ satisfiedRule, onViewDonation }) {
  const subtitle = buildSatisfiedSubtitle(satisfiedRule.satisfaction)

  return (
    <Card className="card card--sm donation-card donation-card--satisfied">
      <Card.Body>
        <Stack
          direction="horizontal"
          gap={2}
          className="align-items-center justify-content-between"
        >
          <Stack direction="horizontal" gap={2} className="align-items-start">
            <div className="donation-card__icon donation-card__icon--success">
              <CheckIcon />
            </div>
            <div>
              <Stack
                direction="horizontal"
                gap={2}
                className="heading--flex flex-sm flex-wrap align-items-center"
              >
                <Card.Title as="h2" className="card-title m-0">
                  {satisfiedRule.name}
                </Card.Title>
                <span className="donation-card__satisfied-badge text-upper text-upper-caption">
                  Satisfied
                </span>
              </Stack>
              <p className="donation-card__satisfied-subtitle m-0 mt-1">
                {subtitle}
              </p>
            </div>
          </Stack>
          {onViewDonation && (
            <button
              type="button"
              className="donation-card__view-link"
              onClick={onViewDonation}
            >
              View &gt;
            </button>
          )}
        </Stack>
      </Card.Body>
    </Card>
  )
}

function resolveSelectedRuleId(selectedRuleId, unmetRules) {
  if (!selectedRuleId || !unmetRules.length) return ''
  return unmetRules.some((rule) => String(rule.id) === String(selectedRuleId))
    ? String(selectedRuleId)
    : ''
}

function resolveActiveRule(unmetRules, selectedRuleId) {
  if (!unmetRules.length) return null
  if (unmetRules.length === 1) return unmetRules[0]
  const resolvedId = resolveSelectedRuleId(selectedRuleId, unmetRules)
  if (!resolvedId) return null
  return unmetRules.find((rule) => String(rule.id) === resolvedId) || null
}

export default function DonationCard({
  unmetRules = [],
  satisfiedRules = [],
  configuredDonations = [],
  selectedRuleId,
  setSelectedRuleId,
  donationAmount,
  setDonationAmount,
  externalPending,
  setExternalPending,
  onApplyDonation,
  ticketsAffected,
  onViewDonation,
}) {
  const [isCustom, setIsCustom] = useState(false)
  const [customAmount, setCustomAmount] = useState('')
  const [selectedAmount, setSelectedAmount] = useState('')
  const [ruleExternalPending, setRuleExternalPending] = useState(false)

  const isMultiRuleFlow = unmetRules.length > 1
  const activeExternalPending = isMultiRuleFlow
    ? ruleExternalPending
    : externalPending
  const setActiveExternalPending = isMultiRuleFlow
    ? setRuleExternalPending
    : setExternalPending

  const resolvedSelectedRuleId = useMemo(
    () => resolveSelectedRuleId(selectedRuleId, unmetRules),
    [selectedRuleId, unmetRules]
  )

  const activeRule = useMemo(
    () => resolveActiveRule(unmetRules, selectedRuleId),
    [unmetRules, selectedRuleId]
  )

  const givingLevels = useMemo(() => {
    const levels =
      activeRule?.givingLevels?.length > 0
        ? activeRule.givingLevels.map(Number)
        : [Number(activeRule?.minimum) || 0]
    const minimum = Number(activeRule?.minimum) || levels[0] || 0
    const merged = [minimum, ...levels.filter((level) => level !== minimum)]
    return [...new Set(merged)].sort((a, b) => a - b)
  }, [activeRule])

  useEffect(() => {
    if (!activeRule) return
    const defaultAmount = String(givingLevels[0] || activeRule.minimum || 0)
    setSelectedAmount(defaultAmount)
    setIsCustom(false)
    setCustomAmount('')
    setDonationAmount(Number(defaultAmount) || 0)
    setRuleExternalPending(false)
    // Only reset when the active rule changes, not when the parent re-renders
    // with a new object reference for the same rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRule?.id])

  useEffect(() => {
    if (!unmetRules.length) return

    if (unmetRules.length === 1) {
      const onlyRuleId = String(unmetRules[0].id)
      if (String(selectedRuleId) !== onlyRuleId) {
        setSelectedRuleId?.(onlyRuleId)
      }
      return
    }

    if (selectedRuleId && !resolvedSelectedRuleId) {
      setSelectedRuleId?.('')
    }
  }, [selectedRuleId, resolvedSelectedRuleId, unmetRules, setSelectedRuleId])

  const configuredRuleIds = useMemo(
    () => new Set(configuredDonations.map((entry) => String(entry.ruleId))),
    [configuredDonations]
  )

  const pendingUnmetRules = useMemo(
    () => unmetRules.filter((rule) => !configuredRuleIds.has(String(rule.id))),
    [unmetRules, configuredRuleIds]
  )

  const hasContent = satisfiedRules.length > 0 || unmetRules.length > 0
  if (!hasContent) return null

  const showRulesPicker =
    isMultiRuleFlow && pendingUnmetRules.length > 0 && !resolvedSelectedRuleId
  const showDetailCard =
    Boolean(activeRule) &&
    !showRulesPicker &&
    (!isMultiRuleFlow || !configuredRuleIds.has(String(activeRule.id)))

  const applyCurrentDonation = (
    amount,
    pendingFlag = activeExternalPending
  ) => {
    if (!isMultiRuleFlow || !activeRule || !onApplyDonation) return false

    const numericAmount = Number(amount) || 0
    const minimum = Number(activeRule.minimum) || 0
    if (!pendingFlag && numericAmount < minimum) return false

    onApplyDonation({
      ruleId: activeRule.id,
      ruleName: activeRule.name,
      amount: numericAmount,
      externalPending: pendingFlag,
    })
    return true
  }

  const description = activeRule
    ? `${formatGivingLevelAmount(
        activeRule.minimum
      )} minimum — required for tickets in ${formatSectionList(
        activeRule.triggerSections
      )} (${activeRule.scopeLabel || 'Per account, per year'})`
    : ''

  const handleSelectAmount = (amount) => {
    setIsCustom(false)
    setSelectedAmount(String(amount))
    setDonationAmount(Number(amount) || 0)
    if (isMultiRuleFlow) {
      applyCurrentDonation(amount)
    }
  }

  const handleSelectCustom = () => {
    setIsCustom(true)
    const nextAmount = customAmount || String(activeRule.minimum)
    if (!customAmount) {
      setCustomAmount(nextAmount)
    }
    setDonationAmount(Number(nextAmount) || 0)
  }

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value)
    setDonationAmount(Number(value) || 0)
  }

  const handleCustomAmountBlur = () => {
    if (!isCustom || !isMultiRuleFlow || activeExternalPending) return
    applyCurrentDonation(customAmount)
  }

  const handleExternalPendingChange = (checked) => {
    setActiveExternalPending(checked)
    if (!isMultiRuleFlow || !activeRule || !checked) return

    const amount = isCustom ? customAmount : selectedAmount
    applyCurrentDonation(amount, true)
  }

  const externalPaymentFieldId = activeRule
    ? `donation-external-payment-${activeRule.id}`
    : 'donation-external-payment'

  return (
    <Stack gap={3}>
      {satisfiedRules.map((satisfiedRule) => (
        <SatisfiedDonationRuleCard
          key={`satisfied-${satisfiedRule.id}`}
          satisfiedRule={satisfiedRule}
          onViewDonation={onViewDonation}
        />
      ))}

      {configuredDonations.map((configuredDonation) => (
        <ConfiguredDonationRuleCard
          key={`configured-${configuredDonation.ruleId}`}
          configuredDonation={configuredDonation}
        />
      ))}

      {showRulesPicker && (
        <DonationFundPicker
          rules={pendingUnmetRules}
          onSelectRule={setSelectedRuleId}
        />
      )}

      {showDetailCard && (
        <>
          {isMultiRuleFlow && (
            <button
              type="button"
              className="donation-card__back-link"
              onClick={() => {
                setSelectedRuleId?.('')
                setDonationAmount(0)
                setRuleExternalPending(false)
                if (!isMultiRuleFlow) {
                  setExternalPending(false)
                }
              }}
            >
              ← Choose fund
            </button>
          )}

          <Card className="card card--sm card-with-border donation-card background-tertiary-light">
            <Card.Body>
              <Stack
                direction="horizontal"
                gap={3}
                className="align-items-start"
              >
                <div className="donation-card__icon">
                  <TargetIcon />
                </div>
                <div className="flex-grow-1 min-w-0">
                  <Stack
                    direction="horizontal"
                    gap={2}
                    className="heading--flex flex-sm flex-wrap mb-1"
                  >
                    <Card.Title as="h2" className="card-title m-0">
                      {activeRule.name}
                    </Card.Title>
                    <span className="badge badge-sm badge--exchanged text-upper text-upper-caption">
                      Required
                    </span>
                  </Stack>
                  <p className="subtitle m-0">{description}</p>
                  {ticketsAffected?.count > 0 && (
                    <p className="subtitle m-0 mt-1">
                      Tickets affected: {ticketsAffected.count}
                      {ticketsAffected.breakdown
                        ? ` (${ticketsAffected.breakdown})`
                        : ''}
                    </p>
                  )}
                </div>
              </Stack>

              <div className="donation-card__amount-grid">
                {givingLevels.map((amount) => {
                  const isSelected =
                    !isCustom && selectedAmount === String(amount)
                  const isMinimum = amount === Number(activeRule.minimum)

                  return (
                    <button
                      key={amount}
                      type="button"
                      className={`donation-card__amount-btn ${
                        isSelected ? 'donation-card__amount-btn--selected' : ''
                      }`}
                      onClick={() => handleSelectAmount(amount)}
                      aria-pressed={isSelected}
                    >
                      <span>{formatGivingLevelAmount(amount)}</span>
                      {isMinimum && (
                        <span className="text-upper text-upper-caption">
                          MIN
                        </span>
                      )}
                    </button>
                  )
                })}
                <button
                  type="button"
                  className={`donation-card__amount-btn ${
                    isCustom ? 'donation-card__amount-btn--selected' : ''
                  }`}
                  onClick={handleSelectCustom}
                  aria-pressed={isCustom}
                >
                  Custom
                </button>
              </div>

              {isCustom && (
                <Form.Group className="form-group mt-2">
                  <InputGroup>
                    <InputGroup.Text id="donation-custom-amount">
                      $
                    </InputGroup.Text>
                    <Form.Control
                      id="donation-custom-amount"
                      name="donation-custom-amount"
                      placeholder="Amount"
                      value={customAmount}
                      onChange={(e) => handleCustomAmountChange(e.target.value)}
                      onBlur={handleCustomAmountBlur}
                      aria-label="Custom donation amount"
                    />
                  </InputGroup>
                </Form.Group>
              )}

              <div className="form-check--with-desc form-check--with-desc--lg donation-card__external">
                <Form.Check
                  type="checkbox"
                  id={externalPaymentFieldId}
                  checked={activeExternalPending}
                  onChange={(e) =>
                    handleExternalPendingChange(e.target.checked)
                  }
                  label="Donor paying externally — mark as pending"
                />
                <small className="subtitle mt-0">
                  {isMultiRuleFlow
                    ? 'Skip charging now. Checking this will save this fund and return you to choose the next one.'
                    : "Skip charging now. You can mark this satisfied from the fan's profile once payment hits the development office."}
                </small>
              </div>

              <div className="split-row split-row-lg donation-card__footer">
                <span>
                  {activeExternalPending
                    ? 'Donation total — will be billed externally'
                    : 'Donation total'}
                </span>
                <span className="normal normal-bold">
                  {formatCurrency(donationAmount)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Stack>
  )
}
